import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, Suspense, lazy } from "react";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import CourseAccessGate from "./components/CourseAccessGate";
import { AuthProvider } from "./context/AuthContext";

// ✅ Lazy imports pour TOUTES les pages
const Accueil = lazy(() => import("./pages/Accueil"));
const Paiement = lazy(() => import("./pages/Paiement"));
const Debut = lazy(() => import("./pages/s-orienter/Debut"));
const Etudes = lazy(() => import("./pages/s-orienter/Etudes"));
const Metiers = lazy(() => import("./pages/s-orienter/Metiers"));
const Trader = lazy(() => import("./pages/s-orienter/metiers/Trader"));
const Structuration = lazy(() => import("./pages/s-orienter/metiers/Structuration"));
const Sales = lazy(() => import("./pages/s-orienter/metiers/Sales"));
const MA = lazy(() => import("./pages/s-orienter/metiers/MA"));
const PrivateEquity = lazy(() => import("./pages/s-orienter/metiers/PrivateEquity"));
const Risk = lazy(() => import("./pages/s-orienter/metiers/Risk"));
const Quant = lazy(() => import("./pages/s-orienter/metiers/Quant"));
const VentureCapital = lazy(() => import("./pages/s-orienter/metiers/VentureCapital"));
const TransactionServices = lazy(() => import("./pages/s-orienter/metiers/TransactionServices"));
const Audit = lazy(() => import("./pages/s-orienter/metiers/Audit"));
const CorpDev = lazy(() => import("./pages/s-orienter/metiers/CorpDev"));
const ECM = lazy(() => import("./pages/s-orienter/metiers/ECM"));
const DCM = lazy(() => import("./pages/s-orienter/metiers/DCM"));
const EquityResearch = lazy(() => import("./pages/s-orienter/metiers/EquityResearch"));
const Calendrier = lazy(() => import("./pages/stages/Calendrier"));
const PreMaster = lazy(() => import("./pages/stages/PreMaster"));
const Cesure = lazy(() => import("./pages/stages/Cesure"));
const Cv = lazy(() => import("./pages/candidature/cv-en-finance"));
const Networking = lazy(() => import("./pages/candidature/networking"));
const Ou = lazy(() => import("./pages/candidature/ou-postuler-en-finance"));
const Process = lazy(() => import("./pages/candidature/process"));
const Ressources = lazy(() => import("./pages/ressources/Ressources"));
const Entretiens = lazy(() => import("./pages/entretien/Entretiens"));
const Contact = lazy(() => import("./pages/Contact"));
const Search = lazy(() => import("./pages/Search"));
const Glossaire = lazy(() => import("./pages/Glossaire"));
const Blog = lazy(() => import("./pages/blog/Blog"));
const BlogPost = lazy(() => import("./pages/blog/BlogPost"));
const Profil = lazy(() => import("./pages/Profil"));
const Cours = lazy(() => import("./pages/cours/Cours"));
const GlossaryTerm = lazy(() => import("./pages/glossary/GlossaryTerm"));
const Lesson = lazy(() => import("./pages/cours/Lesson"));
const Module = lazy(() => import("./pages/cours/Module"));
const ModuleCompare = lazy(() => import("./pages/cours/ModuleCompare"));
const Forum = lazy(() => import("./pages/Forum"));

// 🔥 ScrollToTop
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  return (
    <Router>
      <ScrollToTop />

      <AuthProvider>
        <div className="flex flex-col min-h-screen">
          <Navbar />
          <div className="flex-grow p-4">
            <Suspense fallback={<div>Chargement...</div>}>
              <Routes>
                <Route path="/" element={<Accueil />} />
                <Route path="/paiement" element={<Paiement />} />

                {/* S'orienter */}
                <Route path="/s-orienter/debut" element={<Debut />} />
                <Route path="/s-orienter/etudes" element={<Etudes />} />
                <Route path="/s-orienter/metiers" element={<Metiers />} />
                <Route path="/s-orienter/metiers/trader" element={<Trader />} />
                <Route path="/s-orienter/metiers/structuration" element={<Structuration />} />
                <Route path="/s-orienter/metiers/sales" element={<Sales />} />
                <Route path="/s-orienter/metiers/ma" element={<MA />} />
                <Route path="/s-orienter/metiers/private-equity" element={<PrivateEquity />} />
                <Route path="/s-orienter/metiers/risk" element={<Risk />} />
                <Route path="/s-orienter/metiers/quant" element={<Quant />} />
                <Route path="/s-orienter/metiers/venture-capital" element={<VentureCapital />} />
                <Route path="/s-orienter/metiers/transaction-services" element={<TransactionServices />} />
                <Route path="/s-orienter/metiers/audit" element={<Audit />} />
                <Route path="/s-orienter/metiers/corporate-development" element={<CorpDev />} />
                <Route path="/s-orienter/metiers/ecm" element={<ECM />} />
                <Route path="/s-orienter/metiers/dcm" element={<DCM />} />
                <Route path="/s-orienter/metiers/equity-research" element={<EquityResearch />} />

                {/* Stages */}
                <Route path="/stages/calendrier" element={<Calendrier />} />
                <Route path="/stages/pre-master" element={<PreMaster />} />
                <Route path="/stages/cesure" element={<Cesure />} />

                {/* Candidature */}
                <Route path="/candidature/cv-en-finance" element={<Cv />} />
                <Route path="/candidature/networking" element={<Networking />} />
                <Route path="/candidature/ou-postuler-en-finance" element={<Ou />} />
                <Route path="/candidature/process" element={<Process />} />

                {/* Autres */}
                <Route path="/ressources" element={<Ressources />} />
                <Route path="/entretiens" element={<Entretiens />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/recherche" element={<Search />} />
                <Route path="/glossaire" element={<Glossaire />} />
                <Route path="/glossaire/:slug" element={<GlossaryTerm />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/profil" element={<Profil />} />

                {/* Cours & Forum */}
                <Route path="/cours" element={<CourseAccessGate><Cours /></CourseAccessGate>} />
                <Route path="/cours/:courseId/:lessonId" element={<CourseAccessGate><Lesson /></CourseAccessGate>} />
                <Route path="/cours/:courseId/:lessonId/:moduleId" element={<CourseAccessGate><Module /></CourseAccessGate>} />
                <Route path="/compare/cours/:courseId/:lessonId/:moduleId" element={<ModuleCompare />} />
                <Route path="/forum" element={<Forum />} />
              </Routes>
            </Suspense>
          </div>
          <Footer />
        </div>
      </AuthProvider>
    </Router>
  );
}

export default App;
